import React, { ReactElement } from 'react';
import { useCookies } from 'react-cookie';
import type { Route } from 'next';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import classNames from 'classnames/bind';
import proxyAxios from 'api/proxyAxios';
import { endpoints } from 'api/endpoints';
import RootLayout from 'layouts/RootLayout';
import useWindowSize from 'utils/hooks/useWindowSize';
import { getReadTime } from 'sections/Blog/utils';
import linksNoFollow from 'lib/postNoFollowLinks';
import Breadcrumbs from 'components/Breadcrumbs';
import Post from 'sections/Blog/components/SectionPosts/Post';
import styles from 'pages/blog/BlogPost.module.scss';

const cn = classNames.bind(styles);

const PostCard = dynamic(() => import('sections/Blog/components/SectionPosts/PostCard'));

interface BlogPostProps {
  post: any;
  recommends: Array<any>;
  tags: Array<any>;
  error: any
};

export default function BlogPost({ post, recommends }: BlogPostProps) {
  const [{ user }] = useCookies(['user']);
  const { width } = useWindowSize();

  return (
    <>
      <section id="blog-post">
        {post && (
          <div className={cn('container')}>
            <Breadcrumbs
              mTop={64}
              mode="dark"
              links={[
                { title: 'Blog', href: '/blog' },
                { title: post.title, href: '' as Route},
              ]}
            />
          </div>
        )}

        {width !== 0 && user?.isAdmin ? (
          <Link
            className={cn('button-edit')}
            href={`/admin/article/view/${post.id}/edit`}
          >
            <img
              src='/images/pages/Blog/icon-pencil.svg'
              alt='Edit post button icon'
            />
          </Link>
        ) : (
          <></>
        )}

        {post && <Post post={post} />}
      </section>

      {recommends?.length > 0 && (
        <section className={cn('recommended-posts')}>
          <div className={cn('container')}>
            <h2 className={cn('recommended-posts__heading')}>
              Recommended Posts
            </h2>

            <ul className={cn('recommended-posts__list')}>
              {recommends.map((item) => (
                <PostCard key={item.id} post={item} />
              ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}

export const getServerSideProps = async (context: any) => {
  const url = context.params.title;
  try {
    const { data } = await proxyAxios(`${endpoints.blog.article}?slug=${url}`);

    // const { data: posts } = await proxyAxios(endpoints.blog.previewArticles + '?order=latest');
    // const post = posts.find((d: any) => d.url === url);
    // const { data } = await proxyAxios(`${endpoints.blog.article}?id=${post.id}`);
    const { data: recommends } = await proxyAxios(`${endpoints.blog.recommends}?id=${data.id}`);
    const res = await proxyAxios.post(endpoints.blog.addView, { id: data.id });

    if (res.data.views % 5 === 0) await proxyAxios.post(endpoints.blog.addLike, { id: data.id });

    const meta = {
      title: data.seo_title ? `${data.seo_title} | DF Alliance` : `${data.title} | DF Alliance`,
      description: data.seo_description,
      path: `/blog/${data.url}`,
      authorName: data.author.full_name,
      image: data.image,
      publishDate: data.date,
    };

    const splitContent = data.content.split('<a href="');
    if (splitContent.length !== 0) {
      data.content = splitContent
        .map((item: string, index: number) => {
          if (index === 0) return item;
          const contentLink = item.split('"')[0];
          const link = !!contentLink && linksNoFollow.find((l) => l === contentLink);
          return !!contentLink && !!link ? item.replace(link, `<a href="${link}" rel="nofollow`) : '<a href="' + item;
        })
        .join(' ');
    }

    if (data.content.includes('</video>')) {
      let content = data.content;
      const videoRegex = /<video[^>]*>[^]*?<\/video>/g;
      const videoElement = content.match(videoRegex);

      videoElement.forEach((elem: string) => {
        content = content.replace(
          elem,
          `
          <script type="application/ld+json">
            {
              "@context": "https://schema.org",
              "@type": "VideoObject",
              "name": "${data.title}",
              "description": "${data.seo_description}",
              "uploadDate": "${data.date}T08:00:00+08:00",
              "contentUrl": "${elem.match(/<source\s+src="([^"]+)"/)?.[1] || ''}",
              "thumbnailUrl": [
                "${data.image}"
              ]
            }
          </script>${elem}
          `
        );
      });

      data.content = content;
    }

    const read = getReadTime(data.content);

    return {
      props: {
        post: {
          ...data,
          read,
        },
        ...meta,
        recommends,
      }
    };
  } catch (e) {
    console.log('endpoints.blog-item', e);

    return {
      redirect: {
        destination: '/blog',
        statusCode: 301,
      },
    };
  }
};

BlogPost.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
